import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import type { NextRouter } from 'next/router';

import { ORDER_TYPE_DELIVERY } from 'shared/constants';
import { formatAddress } from 'shared/helpers/address';
import { addDispensaryRoutes, buildBaseRoute } from 'shared/helpers/route-helpers';
import useCart from 'hooks/use-cart';
import useSearchPreferences from 'hooks/use-search-preferences';
import useUI from 'hooks/use-ui';
import { tracker } from 'utils/analytics';
import type { Events, StoreRoutingSource } from 'utils/analytics';
import { GetStoreRoutingCandidatesDocument } from 'src/gql/graphql';
import type { Dispensaries, GetStoreRoutingCandidatesQuery } from 'src/gql/graphql';
import { STORE_ROUTING_FLAG } from 'src/constants/store-routing';
import type { StoreRoutingOutcome } from 'src/constants/store-routing';
import type { CurrentAddress } from 'src/state/ui';
import {
  clearCartForStoreRouting,
  getStoreRoutingAttemptFingerprint,
  getStoreRoutingCartTransitionState,
} from 'src/state/actions/cart';
import {
  StoreRoutingOfferData,
  StoreRoutingOfferResult,
  useStoreRoutingOfferModal,
} from 'components/modals/store-routing-offer';

type CurrentDispensary = Pick<Dispensaries, 'chain' | 'cName' | 'embeddedMenuUrl' | 'id' | 'name'>;

type OfferStoreRouteInput = {
  source: StoreRoutingSource;
  address: CurrentAddress;
  currentDispensary: CurrentDispensary;
  isStillEligible?: () => boolean;
};

type StoreRoutingEventDetails<T> = T extends Events['storeRouting'] ? Omit<T, 'originDispensaryId' | 'source'> : never;

type RoutableAddress = CurrentAddress & { lat: number; lng: number };

type StoreRoutingCandidate = NonNullable<NonNullable<GetStoreRoutingCandidatesQuery['filteredDispensaries']>[number]>;

type ActiveAttempt = {
  cancellation: 'aborted' | 'superseded' | null;
  fingerprint: string;
  participants: Map<symbol, () => boolean>;
  promise: Promise<StoreRoutingOutcome>;
  stage: 'lookup' | 'modal' | 'transition';
};

type AwaitModalResult = StoreRoutingOfferResult | 'route_error' | 'superseded';

let activeAttempt: ActiveAttempt | null = null;

const hasRequiredLookupInput = (address: CurrentAddress, dispensary: CurrentDispensary): address is RoutableAddress =>
  Boolean(
    dispensary.id &&
      dispensary.name &&
      dispensary.chain &&
      address.ln1?.trim() &&
      address.city?.trim() &&
      address.state?.trim() &&
      address.zipcode?.trim() &&
      address.lat !== null &&
      address.lat !== undefined &&
      address.lng !== null &&
      address.lng !== undefined
  );

const toHttpsUrl = (value?: string | null): string | null => {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
};

// Identifies a parent page while ignoring cosmetic URL differences (www., trailing slash, query, hash).
const toPageKey = (value?: string | null): string | null => {
  try {
    const url = new URL(value ?? '');
    return `${url.hostname.replace(/^www\./, '')}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
};

export const createDeferred = <T>(): { promise: Promise<T>; resolve: (value: T) => void } => {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve };
};

async function awaitModalResult(
  openModal: (data: StoreRoutingOfferData) => Promise<StoreRoutingOfferResult>,
  data: StoreRoutingOfferData,
  router: NextRouter
): Promise<AwaitModalResult> {
  const { promise: routeChanged, resolve } = createDeferred<AwaitModalResult>();
  const handleRouteComplete = (): void => resolve('superseded');
  const handleRouteError = (): void => resolve('route_error');
  router.events.on('routeChangeComplete', handleRouteComplete);
  router.events.on('routeChangeError', handleRouteError);

  try {
    return await Promise.race([openModal(data), routeChanged]);
  } finally {
    router.events.off('routeChangeComplete', handleRouteComplete);
    router.events.off('routeChangeError', handleRouteError);
  }
}

export function useStoreRoutingOffer(): (input: OfferStoreRouteInput) => Promise<StoreRoutingOutcome> {
  const flags = useFlags();
  const flagsRef = useRef(flags);
  const owner = useMemo(() => Symbol('store-routing-owner'), []);
  const mountedRef = useRef(true);
  const [queryCandidates] = useLazyQuery(GetStoreRoutingCandidatesDocument);
  const { open: openModal } = useStoreRoutingOfferModal();
  const Cart = useCart();
  const UI = useUI();
  const SearchPreferences = useSearchPreferences();
  const router = useRouter();

  // Keep callbacks stable while eligibility follows the latest flag value.
  flagsRef.current = flags;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const attempt = activeAttempt;
      attempt?.participants.delete(owner);
      if (attempt && activeAttempt === attempt && attempt.participants.size === 0) {
        attempt.cancellation = 'aborted';
      }
    };
  }, [owner]);
  return useCallback(
    ({ address, currentDispensary, isStillEligible, source }: OfferStoreRouteInput): Promise<StoreRoutingOutcome> => {
      if (
        !router ||
        !flagsRef.current[STORE_ROUTING_FLAG] ||
        UI.isDemo ||
        !hasRequiredLookupInput(address, currentDispensary)
      ) {
        return Promise.resolve('not_offered');
      }

      const activeRouter = router;
      const latitude = address.lat;
      const longitude = address.lng;
      const fingerprint = getStoreRoutingAttemptFingerprint(currentDispensary, address, Cart.isMedical);
      const isParticipantEligible = (): boolean =>
        mountedRef.current && Boolean(flagsRef.current[STORE_ROUTING_FLAG]) && isStillEligible?.() !== false;
      if (activeAttempt?.fingerprint === fingerprint && activeAttempt.cancellation === null) {
        activeAttempt.participants.set(owner, isParticipantEligible);
        return activeAttempt.promise;
      }
      if (activeAttempt && activeAttempt.stage !== 'lookup') {
        return Promise.resolve('in_progress');
      }
      if (activeAttempt) {
        activeAttempt.cancellation = 'superseded';
      }

      const routeAtStart = activeRouter.asPath;
      const attempt: ActiveAttempt = {
        cancellation: null,
        fingerprint,
        participants: new Map([[owner, isParticipantEligible]]),
        promise: Promise.resolve('not_offered'),
        stage: 'lookup',
      };
      activeAttempt = attempt;

      attempt.promise = (async (): Promise<StoreRoutingOutcome> => {
        const trackStoreRouting = (payload: StoreRoutingEventDetails<Events['storeRouting']>): void => {
          if (![...attempt.participants.values()].some((isEligible) => isEligible())) {
            return;
          }
          tracker.storeRouting({
            ...payload,
            source,
            originDispensaryId: currentDispensary.id,
          });
        };

        const isAttemptCurrent = (): boolean =>
          attempt.cancellation === null &&
          activeAttempt === attempt &&
          [...attempt.participants.values()].some((isEligible) => isEligible()) &&
          activeRouter.asPath === routeAtStart;

        try {
          const queryResult = await queryCandidates({
            fetchPolicy: 'network-only',
            variables: {
              dispensaryFilter: {
                type: 'Dispensary',
                chain: currentDispensary.chain,
                nearLat: latitude,
                nearLng: longitude,
                deliveryAddress: {
                  ln1: address.ln1,
                  ln2: address.ln2,
                  city: address.city,
                  place: address.place,
                  state: address.state,
                  country: address.country,
                  zipcode: address.zipcode,
                  geometry: { coordinates: [longitude, latitude] },
                },
                distance: 12500,
                deliversToAddress: true,
                openForDelivery: true,
                medical: Cart.isMedical,
                recreational: !Cart.isMedical,
                activeOnly: true,
                includeStealthMode: false,
              },
            },
          });

          if (!isAttemptCurrent()) {
            return attempt.cancellation ?? 'aborted';
          }

          if (queryResult.error) {
            trackStoreRouting({ action: 'failed', failureReason: 'lookup' });
            return 'not_offered';
          }
          // A per-location embed hosts one store per parent page, so switching stores must move the parent page.
          // Only the Dutchie embed script (which sets isIframeSDK on embed:ready) handles route:changing.
          const isPerLocationEmbed = UI.isEmbedded && UI.isIframeSDK && !UI.isChainLocationsEmbed;
          // Compare with the page the shopper is on as well, in case the origin store's URL is unset or stale.
          const currentPageKeys = [currentDispensary.embeddedMenuUrl, UI.iframeParentUrl]
            .map(toPageKey)
            .filter(Boolean);
          const hasParentPage = (location: StoreRoutingCandidate): boolean =>
            Boolean(
              toHttpsUrl(location.embeddedMenuUrl) && !currentPageKeys.includes(toPageKey(location.embeddedMenuUrl))
            );
          const candidate = queryResult.data?.filteredDispensaries?.find(
            (location): location is StoreRoutingCandidate =>
              Boolean(
                location &&
                  location.id !== currentDispensary.id &&
                  location.name &&
                  location.cName &&
                  (!isPerLocationEmbed || hasParentPage(location))
              )
          );
          if (!candidate) {
            return 'not_offered';
          }
          const parentPageUrl = isPerLocationEmbed ? toHttpsUrl(candidate.embeddedMenuUrl) : null;

          const [href] = buildBaseRoute({
            isKiosk: UI.isKiosk,
            isEmbedded: UI.isEmbedded,
            isStoreFront: UI.isStoreFront,
            isMobileEcommApp: UI.isMobileEcommApp,
            dispensary: candidate,
          });
          const [destination] = addDispensaryRoutes({ href, route: href, dispensary: candidate });

          attempt.stage = 'modal';
          const transitionState = getStoreRoutingCartTransitionState(Cart, candidate.id);
          const modalResultPromise = awaitModalResult(
            openModal,
            {
              originStoreName: currentDispensary.name ?? '',
              destinationStoreName: candidate.name ?? '',
              address: formatAddress(address),
              willClearCart: transitionState.willClearCart,
            },
            activeRouter
          );
          trackStoreRouting({
            action: 'viewed',
            destinationDispensaryId: candidate.id,
            cartHadItems: transitionState.willClearCart,
          });
          const modalResult = await modalResultPromise;
          if (modalResult === 'route_error') {
            return 'declined';
          }

          if (modalResult === 'declined') {
            trackStoreRouting({
              action: 'declined',
              destinationDispensaryId: candidate.id,
              cartHadItems: transitionState.willClearCart,
            });
            return 'declined';
          }
          if (modalResult === 'superseded') {
            return 'superseded';
          }
          if (!isAttemptCurrent()) {
            return attempt.cancellation ?? 'aborted';
          }

          trackStoreRouting({
            action: 'accepted',
            destinationDispensaryId: candidate.id,
            cartHadItems: transitionState.willClearCart,
          });
          const latestTransitionState = getStoreRoutingCartTransitionState(Cart, candidate.id);
          if (!transitionState.willClearCart && latestTransitionState.willClearCart) {
            return 'aborted';
          }

          attempt.stage = 'transition';

          clearCartForStoreRouting(Cart, candidate.id, address);
          UI.updateAddress({ location: address, residentialStatus: Cart.address.residentialStatus });
          Cart.setOrderType(ORDER_TYPE_DELIVERY);
          SearchPreferences.setOrderType(ORDER_TYPE_DELIVERY);

          if (parentPageUrl) {
            UI.embeddedEventDispatcher('route:changing', { url: parentPageUrl });
            return 'accepted';
          }

          try {
            const navigated = await activeRouter.push(destination);
            if (!navigated) {
              throw new Error('Store routing navigation returned false');
            }
            return 'accepted';
          } catch {
            if (!Cart.dispensary) {
              Cart.setDispensary(currentDispensary);
            }
            UI.showErnie(`We couldn't open ${candidate.name}. Please try again.`, 'danger');
            trackStoreRouting({
              action: 'failed',
              destinationDispensaryId: candidate.id,
              cartHadItems: transitionState.willClearCart,
              failureReason: 'navigation',
            });
            return 'navigation_failed';
          }
        } catch {
          if (attempt.stage === 'lookup' && isAttemptCurrent()) {
            trackStoreRouting({ action: 'failed', failureReason: 'lookup' });
          }
          if (attempt.stage === 'transition') {
            if (!Cart.dispensary) {
              Cart.setDispensary(currentDispensary);
            }
            UI.showErnie("We couldn't complete the store switch. Please review your cart and try again.", 'danger');
            return 'transition_failed';
          }
          return 'not_offered';
        } finally {
          if (activeAttempt === attempt) {
            activeAttempt = null;
          }
          attempt.participants.clear();
        }
      })();

      return attempt.promise;
    },
    [Cart, SearchPreferences, UI, openModal, owner, queryCandidates, router]
  );
}
